import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Building2, Search } from 'lucide-react'
import { fetchCustomers } from '@/api/customers'
import { CustomerPostureStack } from '@/components/CustomerPostureStack'
import { Card, CardDescription, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

function CustomerCard({ customer }) {
  const total = customer.agent_count ?? 0

  return (
    <Card className="h-full transition-colors hover:border-primary/50">
      <div className="flex h-full items-stretch gap-3 p-6">
        <Link
          to={`/customers/${customer.atera_id}`}
          className="group flex min-w-0 flex-1 flex-col justify-center"
        >
          <div className="flex items-start gap-3">
            <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div className="min-w-0 flex-1 space-y-1">
              <CardTitle className="text-base leading-snug group-hover:text-primary">
                {customer.name}
              </CardTitle>
              {customer.domain ? (
                <CardDescription>{customer.domain}</CardDescription>
              ) : (
                <CardDescription className="text-muted-foreground/60">
                  No domain recorded
                </CardDescription>
              )}
              <p className="pt-1 text-xs text-muted-foreground">
                {total === 0
                  ? 'No devices synced'
                  : `${total} device${total === 1 ? '' : 's'}`}
              </p>
            </div>
          </div>
        </Link>
        <CustomerPostureStack
          customerId={customer.atera_id}
          counts={customer.device_posture}
        />
      </div>
    </Card>
  )
}

function CustomerCardSkeleton() {
  return (
    <Card>
      <div className="flex gap-3 p-6">
        <div className="flex flex-1 items-start gap-3">
          <Skeleton className="h-5 w-5 shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
        <div className="flex flex-col justify-center gap-2.5 border-l border-border pl-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-5 w-10" />
          ))}
        </div>
      </div>
    </Card>
  )
}

export function Customers() {
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')

  useEffect(() => {
    fetchCustomers()
      .then(setCustomers)
      .catch(() => setError('Could not load customers.'))
      .finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return customers
    return customers.filter((customer) => {
      const name = (customer.name || '').toLowerCase()
      const domain = (customer.domain || '').toLowerCase()
      return name.includes(q) || domain.includes(q)
    })
  }, [customers, search])

  return (
    <div className="space-y-6">
      {error && <p className="text-sm text-destructive">{error}</p>}

      {!loading && customers.length > 0 ? (
        <div className="space-y-2 max-w-md">
          <Label htmlFor="customers-search">Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="customers-search"
              placeholder="Customer name or domain…"
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <CustomerCardSkeleton key={i} />
          ))}
        </div>
      ) : customers.length === 0 ? (
        <Card className="py-10 text-center text-sm text-muted-foreground">
          No customers synced yet.
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="py-10 text-center text-sm text-muted-foreground">
          No customers match “{search.trim()}”.
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((customer) => (
            <CustomerCard key={customer.atera_id} customer={customer} />
          ))}
        </div>
      )}
    </div>
  )
}
